import {officialPlugin} from '../../plugins';
import {elementNameConst, shapeTypeConst, stateConst} from '../../conf';
import {getMockTpShapeAttrById, initNodeModel} from '../../data/mock';
import {
  handleAddBackgroundColor,
  handleCommonSizeCompute,
  handleRemoveBackgroundColor
} from './node';
import {getIcon} from './icon';

/**
 * 拓扑插件-拓扑属性处理相关代码
 */

export const canvasAttribute = {
  width: 1920,
  height: 1080,
  backgroundColor: '',
  backgroundImage: '',
  gridEnable: false,
  gridSize: 20,
  gridColor: '#d3d3d3',
  rulerEnable: false,
  rulerColor: ''
};


/**
 * 拓扑插件-属性-处理启用/禁用背景网格插件事件
 * @param that vue实例
 * @param status 状态，true启用/false禁用
 */
export function handleEnableGridPluginEvent(that, status) {
  that.$nextTick(() => {
    const stage = that.getCanvasStage();
    if (status) {
      stage.addPlugin(officialPlugin.initGrid());
    } else {
      const plugins = stage.get(elementNameConst.plugins);
      const grid = plugins?.find(item => item?.constructor?.name === elementNameConst.grid);
      stage.removePlugin(grid);
    }
  });
}


/**
 * 拓扑插件-属性-设置画布背景颜色事件
 * @param that vue 实例
 * @param color 颜色值
 */
export function handleBgColorChangeEvent(that, color) {
  if (color) {
    handleAddBackgroundColor(that, color);
  } else {
    handleRemoveBackgroundColor(that);
  }
}


/**
 * 拓扑插件-属性-处理节点图形属性转换
 * @param shape 图形节点
 */
export function handleAttributeConversion(shape) {

  // 解构 model 通用属性
  const {id, label, size, x, y, style, labelCfg, type} = shape.getModel();
  // 转换宽度和高度
  const {width, height} = handleCommonSizeCompute(size);
  const {fill, stroke, lineWidth, lineDash, shadowColor, shadowBlur, shadowOffsetX, shadowOffsetY, opacity, strokeOpacity} = style;

  // 文字属性解构
  const {position, offset, refX, refY, autoRotate} = labelCfg;

  const itemModel = {
    // 图形类型，节点（1 字体图标、2 图片、3 内置图形），边（1、直线、2、折线、3、曲线）
    type: getNodeTypeStatus(shape),
    // 基础属性
    attr: {id, label, width, height, x, y},
    // 节点、边样式
    itemStyle: {
      commonStyle: {stroke, lineWidth, lineDash, shadowColor, shadowBlur, shadowOffsetX, shadowOffsetY},
      nodeStyle: {fill, opacity},
      edgeStyle: {strokeOpacity}
    },
    labelCfg: {
      commonCfg: {position, style: labelCfg?.style || {}},
      nodeCfg: {offset},
      edgeCfg: {refX, refY, autoRotate}
    }
  };

  // 如果是自定义图形
  if (type === shapeTypeConst.iconFont) {
    handleIconFontAttributeConversion(shape, itemModel);
  }

  return itemModel;
}


/**
 * 拓扑插件-属性-处理自定义节点-字体图形属性转换
 * @param shape 图形节点
 * @param model 节点图形属性
 */
export function handleIconFontAttributeConversion(shape, model) {
  // 如果是自定义图形
  const group = shape.getContainer();
  // 获取背景图形
  const bgAttr = group?.getFirst()?.attr();
  // 获取节点文本
  const labelAttr = group?.getLast()?.attr();

  if (model) {
    if (bgAttr) {
      const {fill, fillOpacity, opacity, stroke, lineWidth, lineDash, shadowColor, shadowBlur, shadowOffsetX, shadowOffsetY} = bgAttr;
      // le_tp_shape_model 组件中 handleShapeAttrConvert 处理了后面覆盖前面
      model.itemStyle.nodeStyle = {
        ...model.itemStyle.nodeStyle,
        fill, fillOpacity, opacity, stroke, lineWidth,
        lineDash, shadowColor, shadowBlur, shadowOffsetX, shadowOffsetY
      };
    }
    
    if (labelAttr) {
      const {fill, fontSize, opacity, textAlign, stroke, lineWidth, shadowColor, shadowBlur, shadowOffsetX, shadowOffsetY} = labelAttr;
      model.labelCfg.nodeCfg.style = {
        ...model.labelCfg.nodeCfg.style,
        fill, fontSize, opacity, textAlign, stroke, lineWidth, shadowColor, shadowBlur, shadowOffsetX, shadowOffsetY
      };
    }
  }
}


/**
 * 拓扑插件-属性-设置图形节点文字、样式等属性改变处理事件
 * @param that vue 实例
 * @param itemModel 图形节点属性值（改变后）
 */
export function handleShapeAttributeChangeEvent(that, itemModel) {
  const shapes = getCustomSelectedShapes(that);
  const stage = that.getCanvasStage();
  const {id, label, x, y, width, height} = itemModel.attr;

  shapes?.forEach(shape => {
    let model = {};
    if (itemModel.iconCfg) {
      const {iconType, iconItem} = itemModel.iconCfg;
      if (iconType === shapeTypeConst.image) {

        model = initNodeModel({
          type: shapeTypeConst.image, iconContent: iconItem?.content
        });

      } else if (iconType === 'shape') {

        model = getMockTpShapeAttrById(iconItem?.elementId);

      } else if (iconType === 'icon') {

        model = initNodeModel({
          type: shapeTypeConst.iconFont, iconContent: iconItem?.content, iconUnicode: getIcon(iconItem?.content)
        });

      }

      model = {...model, id, label, jobId: '', ipAddress: ''};
      // 清空属性
      shape._cfg.model = {};
    } else {

      model = {...shape.getModel(), label, size: [width, height]};
      model.style = itemModel.style;
      model.labelCfg = itemModel.labelCfg;
    }

    if (shapes.length === 1) {
      model.x = x;
      model.y = y;
    }

    // 刷新元素，包括更新元素位置，更新元素样式，清除之前的缓存
    shape.refresh();
    // 图标或属性修改不能撤回
    stage.updateItem(shape, model, false);
  });
}


/**
 * 拓扑插件-属性-获取自定义规则选中图形节点
 * @param that vue 实例
 * @returns shapes 选中图形节点
 */
export function getCustomSelectedShapes(that) {
  const stage = that.getCanvasStage();
  const nodes = stage.findAllByState(elementNameConst.node, stateConst.selected);
  const edges = stage.findAllByState(elementNameConst.edge, stateConst.selected);

  let shapes = [];
  if (nodes.length > 0) {
    // 如果选中的节点，优先执行节点样式修改
    shapes = [...nodes];
  } else if (nodes.length === 0 && edges.length > 0) {
    // 如果没有选择节点，只选择了边
    shapes = [...edges];
  }

  return shapes;
}


/**
 * 拓扑插件-属性-手动触发图形属性标签激活事件
 * @param that vue 实例
 * @param shapes 选中图形
 */
export function triggerShapeAttributeTabActive(that, shapes) {
  if (shapes && !(shapes instanceof Array)) {
    shapes = [shapes];
  }

  setTimeout(() => {
    that.$bus.$emit('custom:shape:attrSelected', shapes || getCustomSelectedShapes(that));
  }, 50);
}


/**
 * 拓扑插件-属性-获取图元素分类状态码
 * @param shape 图形节点
 * @returns status 分类状态码
 */
export function getItemTypeStatus(shape) {
  // 1 node节点、2 edge边、3 combo组
  const statusEnum = {};
  statusEnum[elementNameConst.node] = 1;
  statusEnum[elementNameConst.edge] = 2;
  statusEnum[elementNameConst.combo] = 3;
  return statusEnum[shape?.getType()] || 0;
}


/**
 * 拓扑插件-属性-根据节点属性获取节点类型状态码
 * @param shape 图形节点
 * @returns status 节点类型状态码
 */
export function getNodeTypeStatus(shape) {
  // 1 字体图标、2 图片、3 内置图形
  const model = shape?.getModel();
  const type = model.type || model.typeField;
  if (!model) {
    return 0;
  }

  if (type === shapeTypeConst.iconFont) {
    return 1;
  }

  if (type === shapeTypeConst.image) {
    return 2;
  }

  return 3;
}


/**
 * 拓扑插件-属性-处理节点改变图标事件（可在图标、图片和内置图形中切换）
 * @param iconType 切换类型
 * @param path 选择图形标识
 * @param item 选择图形对象
 */
export function handleNodeChangeIconEvent(iconType, path, item) {





}

